import React, { useRef } from 'react';

import { motion, type MotionStyle } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useIntersection } from 'react-use';

import './ArtifactsSliderSlide.scss';

interface YatArtifactsSliderSlideProps {
    activeIndex: number;
    children: React.ReactNode;
    index: number;
    expanded?: boolean;
}

const slideTransition = {
    type: 'spring',
    damping: 80,
    stiffness: 300,
    mass: 5,
    restDelta: 0.00001,
};

function getSlideStyle(index: number, activeIndex: number, isExpanded: boolean): MotionStyle {
    const indexOffset = index - activeIndex;

    if (isExpanded && indexOffset === 0) {
        return {
            position: 'fixed',
            top: '0vh',
            left: '0vh',
            height: '100vh',
            width: '100vw',
        };
    }

    if (indexOffset == 0) {
        return {
            position: 'absolute',
            top: '10vh',
        };
    }

    return {
        position: 'absolute',
        top: `${100 * indexOffset + 10 - indexOffset * 17}vh`,
    };
}

export const ArtifactsSliderSlide = ({
    index,
    activeIndex,
    children,
    expanded = false,
}: YatArtifactsSliderSlideProps) => {
    const slideRef = useRef(null);
    const intersection = useIntersection(slideRef, { rootMargin: '600px' });
    const isActive = activeIndex === index;
    // don't render offscreen elements
    const shouldRender = intersection?.isIntersecting;

    return (
        <motion.div
            className={`yat-artifacts__slider__slide ${isActive ? 'yat-artifacts__slider__slide--active' : ''}`}
            ref={slideRef}
            transition={{ ...slideTransition, delay: isActive ? 0 : 0.175 }}
            style={getSlideStyle(index, activeIndex, expanded)}
            initial={{
                translateY: '25vh',
                scale: 0.925,
                opacity: 0,
            }}
            animate={{
                translateY: '0vh',
                opacity: isActive ? 1 : 0.5,
                scale: isActive ? 1 : 0.925,
                borderRadius: expanded ? 0 : undefined,
            }}
            layout
        >
            {shouldRender && children}
            {!expanded && (
                <Link to="?expanded=true">
                    <div className="yat-artifacts__slider__slide__expand" />
                </Link>
            )}
        </motion.div>
    );
};
